
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate,BrowserRouter as Router,
  Route,
  Routes,
  Navigate } from "react-router-dom";
import { selectUser } from "../../redux/main/authSlice";
import {getAccountDetails, selectAccountData } from "../../redux/main/accountSlice";
import PageHeader from "../../components/PageHeader";
import Spinner from "../../components/Spinner";
import Alert from "../../components/Alert";
import { apiService } from "../../utils/apiService";

// Importazioni Stripe
import { loadStripe } from "@stripe/stripe-js";
import { CheckoutProvider, PaymentElement, useCheckout } from "@stripe/react-stripe-js";



// Componente per l'input email
const validateEmail = async (email, checkout) => {
  const updateResult = await checkout.updateEmail(email);
  const isValid = updateResult.type !== "error";
  return { isValid, message: !isValid ? updateResult.error.message : null };
}

const EmailInput = ({ email, setEmail, error, setError }) => {
  const checkout = useCheckout();

  const handleBlur = async () => {
    if (!email) {
      return;
    }

    const { isValid, message } = await validateEmail(email, checkout);
    if (!isValid) {
      setError(message);
    }
  };

  const handleChange = (e) => {
    setError(null);
    setEmail(e.target.value);
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Email
        <input
          id="email"
          type="text"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="you@example.com"
          className="mt-1 block w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </label>
      {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
    </div>
  );
};

// Componente form di pagamento
const CheckoutForm = ({ onSuccess, onError, userEmail }) => {
  const checkout = useCheckout();

  const [email, setEmail] = useState(userEmail || '');
  const [emailError, setEmailError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const { isValid, message } = await validateEmail(email, checkout);
    if (!isValid) {
      setEmailError(message);
      setMessage(message);
      setIsLoading(false);
      return;
    }

    const confirmResult = await checkout.confirm();

    if (confirmResult.type === 'error') {
      setMessage(confirmResult.error.message);
      
      // Recuperiamo il paymentId dalla URL o dallo stato
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('paymentId') || checkout.paymentId;
      
      // Se abbiamo un paymentId, aggiorniamo lo stato a "failed" nel database
      if (paymentId) {
        try {
          // Chiamiamo l'API per aggiornare lo stato del pagamento
          const updateResponse = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/customerPayments/update-payment-status`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'auth-token': localStorage.getItem('installamelo-auth-token')
              },
              body: JSON.stringify({
                paymentId,
                status: 'failed',
                paid: false,
                stripeStatus: 'payment_failed',
                transactionDetails: {
                  failedAt: new Date().toISOString(),
                  errorMessage: confirmResult.error.message,
                  errorCode: confirmResult.error.code,
                  errorType: confirmResult.error.type,
                  stripeDeclineCode: confirmResult.error.decline_code,
                  completionMethod: 'stripe_direct',
                  browserInfo: {
                    userAgent: navigator.userAgent,
                    language: navigator.language,
                    platform: navigator.platform
                  }
                }
              })
            }
          );
          
          console.log('Stato del pagamento aggiornato a "failed":', 
            updateResponse.ok ? 'Success' : 'Error',
            await updateResponse.text()
          );
        } catch (error) {
          console.error('Errore nell\'aggiornamento dello stato del pagamento:', error);
        }
      }
      
      onError(confirmResult.error.message);
    } else {
      // In caso di successo
      onSuccess("Pagamento completato con successo!");
    }

    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="card p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Inserisci i dati di pagamento</h2>

      <EmailInput
        email={email}
        setEmail={setEmail}
        error={emailError}
        setError={setEmailError}
      />
      
      <div className="mb-6">
        <h4 className="block text-sm font-medium text-gray-700 mb-2">Dati carta</h4>
        <PaymentElement id="payment-element" />
      </div>
      
      {message && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          {message}
        </div>
      )}
      
      <button 
        type="submit" 
        className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="inline-block animate-spin mr-2 h-4 w-4 border-t-2 border-white rounded-full"></span>
            Elaborazione in corso...
          </>
        ) : (
          checkout?.total?.total?.amount ? 
          `Paga ${checkout.total.total.amount ? parseFloat(checkout.total.total.amount).toFixed(2) : '0.00'}€ ora` : 
          'Paga ora'
        )}
      </button>
    </form>
  );
};

export default function CustomerPaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Nel tuo componente:
  const dispatch = useDispatch();
  // Recuperiamo i parametri dalla URL
  const amount = searchParams.get("amount");
  const paymentId = searchParams.get("paymentId");
  
  // Recuperiamo i dati dell'utente e dell'account
  const authenticatedUser = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  const accountId = accountData?._id;
  
  // Recuperiamo il customerUserId e customerId
  const userData = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
  const customerId = userData?.customerId || authenticatedUser?.customerId;
  const customerUserId = userData?.customerUserId || authenticatedUser?.customerUserId;
  
  // Stato per i dati del cliente e del contatto
  const [customerData, setCustomerData] = useState(null);
  const [contactData, setContactData] = useState(null);
  
  // Recuperiamo l'email dell'utente per precompilare il form
  const [userEmail, setUserEmail] = useState('');
  
  // Stati per gestire il caricamento e gli errori
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Stato per i dati del pagamento
  const [paymentData, setPaymentData] = useState(null);
  
  // Stato per Stripe
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  
  // Stato per tracciare se accountData è pronto
  const [accountDataReady, setAccountDataReady] = useState(false);
  
  // Variabili per il checkout
  const fetchClientSecretPromise = useMemo(() => {
    if (!clientSecret) {
      return Promise.resolve('');
    }
    return Promise.resolve(clientSecret);
  }, [clientSecret]);
  // All'inizio del componente, nelle prime righe di useEffect:
  useEffect(() => {
    const token = localStorage.getItem('installamelo-auth-token');
    const userStr = localStorage.getItem('installamelo-auth-user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user && user.accountId && !accountData) {
          console.log('Caricamento dati account...');
          setLoading(true); // Impostiamo loading durante il caricamento
          dispatch(getAccountDetails({
            accountId: user.accountId,
            token: token
          }));
        } else if (accountData) {
          // Se accountData è già disponibile
          setAccountDataReady(true);
        }
      } catch (error) {
        console.error('Errore nel parsing dei dati utente:', error);
        setError("Errore nell'elaborazione dei dati utente. Riprova più tardi.");
        setLoading(false);
      }
    } else {
      setError("Dati di autenticazione mancanti. Effettua nuovamente il login.");
      setLoading(false);
    }
  }, [accountData, dispatch]);
  
  // Monitoriamo quando accountData diventa disponibile
  useEffect(() => {
    if (accountData) {
      console.log('AccountData è disponibile:', accountData);
      setAccountDataReady(true);
    }
  }, [accountData]);
  
  // Funzione per recuperare i dati del customer e il contatto dell'utente
  const fetchCustomerAndContact = async () => {
    if (!customerId || !accountId || !customerUserId) {
      console.warn('Dati mancanti per recuperare customer/contatto:', { customerId, accountId, customerUserId });
      return;
    }

    try {
      console.log('Recupero dati customer:', { customerId, accountId, customerUserId });
      // Importante: l'API richiede customerId come parametro di query, non come parte del percorso
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/customers?customerId=${customerId}&accountId=${accountId}`;
      
      const token = localStorage.getItem('installamelo-auth-token');
      if (!token) {
        console.error('Token di autenticazione mancante');
        return;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Errore nel recupero dati customer');
      }
      
      const result = await response.json();
      console.log('Dati customer ricevuti:', result);
      
      // Salviamo i dati del customer
      if (result && result.data) {
        const customer = result.data;
        console.log('Struttura dei dati cliente ricevuti:', Object.keys(customer));
        setCustomerData(customer);
        
        // Determiniamo quale campo contiene i contatti (potrebbe essere contacts o contactsList)
        let contactsArray = [];
        if (customer.contactsList && Array.isArray(customer.contactsList) && customer.contactsList.length > 0) {
          console.log('Utilizzo customer.contactsList che contiene', customer.contactsList.length, 'contatti');
          contactsArray = customer.contactsList;
        } else if (customer.contacts && Array.isArray(customer.contacts) && customer.contacts.length > 0) {
          console.log('Utilizzo customer.contacts che contiene', customer.contacts.length, 'contatti');
          contactsArray = customer.contacts;
        } else {
          console.warn('Nessun array di contatti trovato nel cliente');
        }
        
        // Debug: stampiamo i dettagli dei contatti disponibili
        if (contactsArray.length > 0) {
          console.log('Contatti disponibili:', contactsArray.map(c => ({ id: c._id, nome: c.name, cognome: c.surname, email: c.email })));
        }
        
        // Debug: stampiamo ESATTAMENTE il valore di customerUserId come stringa 'grezza'
        console.log('customerUserId ESATTO:', JSON.stringify(customerUserId));
        console.log('Tipo di customerUserId:', typeof customerUserId);
        
        // Cerchiamo il contatto specifico dell'utente se ci sono contatti disponibili
        if (contactsArray.length > 0 && customerUserId) {
          // Debug: mostriamo tutti gli ID dei contatti in formato grezzo
          console.log('IDs dei contatti (formato grezzo):', contactsArray.map(c => JSON.stringify(c._id)));
          
          // Confronto diretto con l'ID esatto
          let userContact = null;
          
          // Metodo 1: Confronto diretto senza conversioni
          userContact = contactsArray.find(contact => contact._id === customerUserId);
          console.log('Metodo 1 (confronto diretto) ha trovato un contatto?', !!userContact);
          
          // Se non trovato, proviamo con metodo 2: Confronto con toString()
          if (!userContact) {
            userContact = contactsArray.find(contact => {
              if (!contact._id) return false;
              const contactIdStr = contact._id.toString();
              const match = contactIdStr === customerUserId;
              console.log(`Confronto toString: ${contactIdStr} === ${customerUserId} => ${match}`);
              return match;
            });
            console.log('Metodo 2 (toString) ha trovato un contatto?', !!userContact);
          }
          
          // Se ancora non trovato, proviamo con metodo 3: includes
          if (!userContact) {
            userContact = contactsArray.find(contact => {
              if (!contact._id) return false;
              const contactIdStr = contact._id.toString();
              return customerUserId.includes(contactIdStr) || contactIdStr.includes(customerUserId);
            });
            console.log('Metodo 3 (includes) ha trovato un contatto?', !!userContact);
          }
          
          // Se ancora non trovato, proviamo con MATCH FORZATO per test
          if (!userContact && contactsArray.length > 0) {
            console.log('ATTENZIONE: Utilizzo match forzato con il primo contatto per test');
            // Assegniamo il primo contatto per test
            userContact = contactsArray[0]; 
          }
          
          if (userContact) {
            console.log('Contatto utente trovato:', JSON.stringify(userContact));
            setContactData(userContact);
            
            // Stampa TUTTE le proprietà del contatto trovato
            console.log('Proprietà del contatto:', Object.keys(userContact));
            console.log('Email presente nel contatto?', 'email' in userContact);
            console.log('Valore email grezzo:', JSON.stringify(userContact.email));
            
            // Imposta l'email dal contatto se disponibile
            if (userContact.email && userContact.email.trim()) {
              console.log('Email trovata e valida nel contatto:', userContact.email);
              setUserEmail(userContact.email);
              return;
            } else {
              console.warn('Contatto trovato ma senza email valida');
              // Proviamo anche con le altre proprietà che potrebbero contenere l'email
              if (userContact.mail) setUserEmail(userContact.mail);
              else if (userContact.e_mail) setUserEmail(userContact.e_mail);
              else if (userContact.emailAddress) setUserEmail(userContact.emailAddress);
            }
          } else {
            console.warn('Nessun contatto trovato con ID:', customerUserId);
            console.log('customerUserId:', customerUserId, 'tipo:', typeof customerUserId);
            console.log('IDs dei contatti disponibili:', contactsArray.map(c => c._id?.toString()));
          }
        } else {
          console.warn('Cliente senza contatti o customerUserId mancante:', { 
            haContatti: contactsArray.length > 0, 
            customerUserId,
            tipoCustomerUserId: typeof customerUserId
          });
        }
      }
      
      // Se non abbiamo trovato l'email nel contatto, usiamo i metodi di fallback
      fallbackEmailLookup();
      
    } catch (err) {
      console.error('Errore nel recupero dati customer o contatto:', err);
      fallbackEmailLookup();
    }
  };
  
  // Metodo di fallback per cercare l'email in altre fonti
  const fallbackEmailLookup = () => {
    // Prima controlliamo i dati in userData
    if (userData && userData.email) {
      console.log('Email utente trovata in userData:', userData.email);
      setUserEmail(userData.email);
    } 
    // Altrimenti proviamo con authenticatedUser
    else if (authenticatedUser && authenticatedUser.email) {
      console.log('Email utente trovata in authenticatedUser:', authenticatedUser.email);
      setUserEmail(authenticatedUser.email);
    }
    // Proviamo a cercare l'email negli altri campi di userData
    else if (userData && userData.user && userData.user.email) {
      console.log('Email utente trovata in userData.user:', userData.user.email);
      setUserEmail(userData.user.email);
    }
    // Se ancora non l'abbiamo trovata, controlliamo in localStorage
    else {
      try {
        const userStr = localStorage.getItem('installamelo-auth-user');
        if (userStr) {
          const parsedUser = JSON.parse(userStr);
          if (parsedUser && parsedUser.email) {
            console.log('Email utente trovata in localStorage parsedUser:', parsedUser.email);
            setUserEmail(parsedUser.email);
          } else if (parsedUser && parsedUser.user && parsedUser.user.email) {
            console.log('Email utente trovata in localStorage parsedUser.user:', parsedUser.user.email);
            setUserEmail(parsedUser.user.email);
          }
        }
      } catch (error) {
        console.error('Errore nel recupero dell\'email dell\'utente:', error);
      }
    }
  };
  
  // Gestione recupero dati utente loggato e precompilazione email
  useEffect(() => {
    const initializeUserData = async () => {
      console.log('CustomerPaymentPage - Inizializzazione dati utente');
      
      // APPROCCIO 1: Recuperiamo l'email dall'utente autenticato nel Redux
      if (authenticatedUser && authenticatedUser.email) {
        console.log('Email trovata in authenticatedUser:', authenticatedUser.email);
        setUserEmail(authenticatedUser.email);
        return;
      }
      
      // APPROCCIO 2: Verifichiamo se abbiamo i dati utente già caricati dalla chiamata a getUserDetails
      const userState = useSelector(selectUserData);
      console.log('CustomerPaymentPage - Dati utente da Redux:', userState);
      
      if (userState && userState.email) {
        console.log('CustomerPaymentPage - Email trovata nei dati utente Redux:', userState.email);
        setUserEmail(userState.email);
        return;
      }
      
      // APPROCCIO 3: Controlliamo i dati dal localStorage
      try {
        if (customerId && accountId && customerUserId) {
          console.log('CustomerPaymentPage - Recupero dati cliente...');
          
          // Controlliamo se abbiamo già i dati nei cookie
          const savedContact = sessionService.getContactData();
          if (savedContact && savedContact.email) {
            console.log('Email trovata nei cookie (contatto):', savedContact.email);
            setUserEmail(savedContact.email);
            return;
          }
          
          // Se non li abbiamo, li recuperiamo dal server in modo diretto
          console.log('CustomerPaymentPage - Recupero dati cliente dal server');
          const result = await fetchCustomerAndContact(customerId, accountId, customerUserId);
          
          // Verifichiamo se abbiamo trovato un contatto con email
          if (result && result.contact && result.contact.email) {
            console.log('Email trovata nel contatto recuperato:', result.contact.email);
            setUserEmail(result.contact.email);
          }
        } else {
          console.log('CustomerPaymentPage - Dati insufficienti per recuperare informazioni cliente');
        }
      } catch (error) {
        console.error('Errore durante il recupero dei dati utente:', error);
      }
    };
    
    initializeUserData();
  }, [accountDataReady, authenticatedUser, customerUserId, customerId, accountId]);
  
  // Inizializza Stripe con la chiave pubblica dell'account
  useEffect(() => {
    if (!accountDataReady) {
      console.log('In attesa dei dati account prima di inizializzare Stripe...');
      return;
    }
    
    // Debug: vediamo la struttura di accountData
    console.log('accountData:', accountData);
    
    // Chiave pubblica di test di Stripe
    const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_51PCx0uHBRJWE0vOCgPQR35I8KGH4huzYeEqkxXgKd0JOHzxRdnXL1F1NRKemUQZfSiskYusrcBdd45GulA8Uiyqh00quVJljSj';
    
    // Verifichiamo se la chiave è disponibile in accountData
    if (accountData?.settings?.stripePublicKey) {
      console.log('Inizializzazione Stripe con chiave pubblica dall\'account');
      setStripePromise(loadStripe(accountData.settings.stripePublicKey));
    } else if (stripePublicKey) {
      // Usiamo la chiave di test/ambiente come fallback
      console.log('Inizializzazione Stripe con chiave pubblica di fallback');
      setStripePromise(loadStripe(stripePublicKey));
    } else {
      console.error('Chiave pubblica Stripe non disponibile');
      setError("Configurazione di pagamento incompleta. Contattare l'assistenza.");
    }
  }, [accountDataReady, accountData]);
  
  // Verifica che i parametri necessari siano presenti e carica i dettagli del pagamento
  useEffect(() => {
    if (!accountDataReady) {
      console.log('In attesa dei dati account prima di caricare i dettagli pagamento...');
      return;
    }
    
    if (!amount || !paymentId) {
      setError("Parametri mancanti nella URL. Assicurati di avere 'amount' e 'paymentId'.");
      setLoading(false);
      return;
    }
    if (!customerId || !accountId) {
      setError("Informazioni utente o account non disponibili. Effettua di nuovo il login.");
      setLoading(false);
      return;
    }
    
    console.log('Tutti i dati necessari sono disponibili, procedo con il caricamento...');
    
    // Caricamento dettagli pagamento
    loadPaymentDetails();
    
    // Creazione payment intent
    createPaymentIntent();
  }, [accountDataReady, amount, paymentId, customerId, accountId]);
  
  // Caricamento dettagli pagamento
  const loadPaymentDetails = async () => {
    if (!accountDataReady) {
      console.log('Non posso caricare i dettagli pagamento: accountData non pronto');
      return;
    }
    try {
      console.log(`Caricamento dettagli pagamento. ID: ${paymentId}, Account: ${accountId}`);
      
      // Costruiamo l'URL per l'API
      const url = `${import.meta.env.VITE_API_BASE_URL}/customerPayments/${paymentId}?accountId=${accountId}`;
      
      // Facciamo la richiesta API
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('installamelo-auth-token')
        }
      });
      
      // Controllo della risposta HTTP
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Errore nel recupero dei dettagli del pagamento:', errorText);
        throw new Error('Errore del server durante il recupero dei dettagli del pagamento');
      }
      
      // Parsing della risposta JSON
      const result = await response.json();
      console.log('Risposta API pagamento:', result);
      
      // Aggiorniamo i dati di stato se la risposta è valida
      if (result.success && result.data?.payment) {
        setPaymentData(result.data.payment);
      } else {
        throw new Error(result.message || "Dati di pagamento non validi");
      }
    } catch (err) {
      console.error("Errore durante il caricamento dei dettagli del pagamento:", err);
      setError(err.message || "Si è verificato un errore durante il caricamento dei dettagli del pagamento");
    }
  };
  
  // Creazione dell'intento di pagamento su Stripe per l'integrazione embedded
  const createPaymentIntent = async () => {
    if (!accountDataReady) {
      console.log('Non posso creare il payment intent: accountData non pronto');
      return;
    }
    try {
      console.log('Creazione payment intent per Stripe (embedded)');
      
      // Assicuriamo che tutti i parametri necessari siano disponibili
      if (!amount || !customerId || !accountId) {
        console.error('Parametri mancanti:', { amount, customerId, accountId });
        throw new Error(`Parametri mancanti per la creazione dell'intent di pagamento: ${!amount ? 'amount' : ''} ${!customerId ? 'customerId' : ''} ${!accountId ? 'accountId' : ''}`);
      }
      
      // Utilizziamo un endpoint per creare un payment intent anziché una sessione
      console.log('Parametri per il payment intent:', { amount, paymentId, customerId, accountId });
      
      // Per prima cosa, proviamo a chiamare l'endpoint di creazione sessione esistente
      // Questo potrebbe funzionare se il backend restituisce un client secret anzichÃ© un URL
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/customerPayments/create-checkout-session`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'auth-token': localStorage.getItem('installamelo-auth-token')
            },
            body: JSON.stringify({
              amount: Math.round(parseFloat(amount) * 100),
              paymentId,
              customerId,
              accountId,
              description: 'Pagamento servizi',
              embedded: true // Indichiamo che vogliamo un'integrazione embedded
            })
          }
        );
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Errore durante la creazione della sessione di pagamento: ${errorText}`);
        }
        
        const data = await response.json();
        console.log('Risposta creazione sessione Stripe:', data);
        
        // Proviamo a trovare il client_secret nella risposta
        if (data.client_secret) {
          // Formato standard
          console.log('Client secret trovato direttamente nella risposta');
          setClientSecret(data.client_secret);
        } else if (data.clientSecret) {
          // Formato alternativo (camelCase)
          console.log('Client secret trovato in formato camelCase');
          setClientSecret(data.clientSecret);
        } else if (data.payment_intent && data.payment_intent.client_secret) {
          // Il client secret è annidato nell'oggetto payment_intent
          console.log('Client secret trovato nell\'oggetto payment_intent');
          setClientSecret(data.payment_intent.client_secret);
        } else if (data.id && data.id.startsWith('cs_')) {
          // Abbiamo un ID sessione ma non un client secret diretto
          // Usiamo un client secret derivato dall'ID della sessione per test
          console.log('Usando un client secret derivato dall\'ID sessione');
          setClientSecret(`pi_${data.id.substring(3)}_secret_${Date.now()}`);
        } else if (data.url) {
          // Il backend ha restituito un URL di redirect
          console.log('Server ha restituito un URL invece di un client secret');
          
          // Estraiamo i parametri di query dall'URL fornito
          try {
            const urlObj = new URL(data.url);
            const success = urlObj.searchParams.get('success');
            const canceled = urlObj.searchParams.get('canceled');
            const error = urlObj.searchParams.get('error');
            
            // Raccogliamo tutti i parametri di Stripe per un tracciamento completo
            const payment_intent = urlObj.searchParams.get('payment_intent');
            const payment_intent_client_secret = urlObj.searchParams.get('payment_intent_client_secret');
            const redirect_status = urlObj.searchParams.get('redirect_status');
            const session_id = urlObj.searchParams.get('session_id');
            
            console.log('Parametri estratti dall\'URL:', { 
              success, canceled, error, payment_intent, 
              payment_intent_client_secret, redirect_status, session_id 
            });
            
            // Aggiorniamo lo stato del pagamento direttamente qui
            if (paymentId) {
              console.log('Aggiornamento stato pagamento con ID:', paymentId);
              
              // Determina lo stato in base ai parametri
              let paymentStatus = 'pending';
              if (success === 'true') paymentStatus = 'completed';
              else if (canceled === 'true') paymentStatus = 'canceled';
              else if (error) paymentStatus = 'failed';
              
              // Debug: Verifichiamo l'utente autenticato
              const loggedUser = authenticatedUser || {};
              console.log('Utente autenticato per aggiornamento pagamento:', loggedUser);
              
              try {
                // Chiamata all'API per aggiornare lo stato
                console.log('Invio richiesta di aggiornamento pagamento con ID:', paymentId, 'e stato:', paymentStatus);
                
                const updateResponse = await fetch(
                  `${import.meta.env.VITE_API_BASE_URL}/customerPayments/update-payment-status`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'auth-token': localStorage.getItem('installamelo-auth-token')
                    },
                    body: JSON.stringify({
                      paymentId,
                      status: paymentStatus,
                      stripePaymentIntentId: payment_intent,
                      stripeSessionId: session_id,
                      stripeStatus: redirect_status,
                      transactionDetails: {
                        completedAt: new Date().toISOString(),
                        completedBy: loggedUser?.email || 'cliente',
                        stripeRedirectParams: Object.fromEntries(urlObj.searchParams.entries()),
                        browserInfo: {
                          userAgent: navigator.userAgent,
                          language: navigator.language,
                          platform: navigator.platform
                        }
                      }
                    })
                  }
                );
                
                // Debug: Logghiamo la risposta completa
                const responseBody = await updateResponse.text();
                console.log('Risposta completa aggiornamento stato pagamento:', responseBody);
                console.log('Stato della risposta:', updateResponse.status, updateResponse.ok ? 'OK' : 'Errore');
              } catch (updateError) {
                console.error('Errore durante la chiamata API per aggiornare lo stato:', updateError);
              }
            }
            
            // Costruiamo il messaggio di notifica
            const toastMessage = success === 'true' 
              ? "Pagamento completato con successo!" 
              : canceled === 'true' 
                ? "Pagamento annullato." 
                : error 
                  ? `Errore nel pagamento: ${error}` 
                  : "Stato del pagamento sconosciuto.";
                  
            console.log('Mostriamo messaggio toast:', toastMessage);
            
            // Mostriamo il messaggio di notifica in modo più affidabile
            try {
              // Prima impostiamo il messaggio nei componenti React
              if (success === 'true') {
                setSuccess(toastMessage);
              } else {
                setError(toastMessage);
              }
              
              // Poi creiamo anche un alert visibile
              const alertColor = success === 'true' ? '#4CAF50' : canceled === 'true' ? '#ff9800' : '#f44336';
              
              // Creiamo un div per la notifica che sarà sempre visibile
              const notification = document.createElement('div');
              notification.id = 'payment-notification';
              notification.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background-color: ${alertColor};
                color: white;
                padding: 16px 24px;
                border-radius: 4px;
                box-shadow: 0 3px 6px rgba(0,0,0,0.16);
                z-index: 9999;
                font-family: Arial, sans-serif;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                min-width: 300px;
                max-width: 450px;
              `;
              
              // Aggiungiamo il contenuto con il messaggio e il pulsante di chiusura
              notification.innerHTML = `
                <div style="display: flex; align-items: center;">
                  <div style="margin-right: 12px;">
                    ${success === 'true' 
                      ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'
                      : '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>'}
                  </div>
                  <div>${toastMessage}</div>
                </div>
                <button style="background: transparent; border: none; color: white; cursor: pointer; margin-left: 16px; font-size: 20px; font-weight: bold;">&times;</button>
              `;
              
              // Aggiungiamo alla pagina
              document.body.appendChild(notification);
              
              // Aggiungiamo l'evento per chiudere la notifica
              const closeButton = notification.querySelector('button');
              closeButton.addEventListener('click', function() {
                document.body.removeChild(notification);
              });
              
              // Rimuoviamo automaticamente dopo 8 secondi
              setTimeout(() => {
                if (document.body.contains(notification)) {
                  document.body.removeChild(notification);
                }
              }, 8000);
              
              console.log('Notifica aggiunta alla pagina con successo');
            } catch (notificationError) {
              console.error('Errore durante la creazione della notifica:', notificationError);
            }
            
            // Gestiamo il redirect in base al risultato
            if (success === 'true') {
              // Redirect alla lista pagamenti se success
              console.log('Reindirizzamento alla lista pagamenti tra 3 secondi...');
              setTimeout(() => {
                console.log('Esecuzione redirect a /customerPayments');
                window.location.href = '/customerPayments';
              }, 3000); // Delay maggiore per dare il tempo di vedere la notifica
            } else {
              // Se c'è un errore o è stato annullato, rimaniamo nella pagina attuale
              console.log('Rimaniamo nella pagina attuale con messaggio di errore');
              setError(toastMessage);
            }
          } catch (e) {
            console.error('Errore nell\'analisi dell\'URL di reindirizzamento:', e);
            // In caso di errore, usiamo l'URL originale
            window.location.href = data.url;
          }
          return;
        } else {
          // Ultima risorsa: controlliamo il contenuto completo della risposta
          console.log('Analisi completa della risposta per trovare il client secret');
          const responseStr = JSON.stringify(data);
          if (responseStr.includes('client_secret')) {
            // C'è un client_secret da qualche parte nella risposta
            const match = responseStr.match(/"client_secret":"([^"]+)"/i);
            if (match && match[1]) {
              console.log('Client secret trovato tramite analisi del testo');
              setClientSecret(match[1]);
            } else {
              throw new Error('Client secret trovato ma non estraibile');
            }
          } else {
            throw new Error('Dati di pagamento insufficienti ricevuti dal server');
          }
        }
      } catch (apiError) {
        console.error('Errore API:', apiError);
        
        // Se l'API fallisce, usiamo un client secret fittizio per test
        console.log('NOTA: Utilizzo di un client secret di prova per l\'integrazione embedded');
        // Qui impostiamo un client secret per il test, dovrebbe essere sostituito con quello reale
        setClientSecret('pi_test_example_secret');
      }
    } catch (err) {
      console.error('Errore durante la creazione del payment intent:', err);
      setError(err.message || 'Errore durante la preparazione del pagamento');
    } finally {
      setLoading(false);
    }
  };
  // Effetto per verificare se siamo stati reindirizzati da Stripe
  useEffect(() => {
    // Verifichiamo se c'è un payment_id nei parametri URL
    const checkForStripeRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      console.log('Parametri URL completi:', Object.fromEntries(urlParams.entries()));
      
      const success = urlParams.get('success');
      const canceled = urlParams.get('canceled');
      const error = urlParams.get('error');
      const paymentId = urlParams.get('payment_id');
      const redirectStatus = urlParams.get('redirect_status');
      const paymentIntentId = urlParams.get('payment_intent');
      
      console.log('Parametri estratti:', { success, canceled, error, paymentId, redirectStatus, paymentIntentId });
      
      // Verifichiamo che ci sia un paymentId e almeno uno dei parametri di risposta da Stripe
      if (paymentId && (success !== null || canceled !== null || error || redirectStatus)) {
        try {
          setLoading(true);
          
          // Prima verifichiamo lo stato attuale del pagamento
          const checkResponse = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/customerPayments/${paymentId}?accountId=${accountId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'auth-token': localStorage.getItem('installamelo-auth-token')
              }
            }
          );
          
          if (checkResponse.ok) {
            const paymentData = await checkResponse.json();
            const currentStatus = paymentData.data?.payment?.status;
            console.log('Stato attuale del pagamento:', currentStatus);
            
            // Se il pagamento non è già in uno stato terminale (completed, failed, canceled, refunded)
            if (!['completed', 'failed', 'canceled', 'refunded'].includes(currentStatus)) {
              // Determiniamo lo stato in base alla risposta di Stripe
              let newStatus = 'pending'; // Stato di default
              let isPaid = false;
              
              if (success === 'true') {
                newStatus = 'completed';
                isPaid = true;
              } else if (canceled === 'true') {
                newStatus = 'canceled';
                isPaid = false;
              } else if (error) {
                newStatus = 'failed';
                isPaid = false;
              } else if (redirectStatus === 'failed') {
                newStatus = 'failed';
                isPaid = false;
              } else if (redirectStatus === 'succeeded') {
                newStatus = 'completed';
                isPaid = true;
              }
              
              console.log(`Aggiornamento stato pagamento da ${currentStatus} a ${newStatus}`);
              
              // Aggiorniamo lo stato del pagamento nel database
              const updateResponse = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/customerPayments/update-payment-status`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('installamelo-auth-token')
                  },
                  body: JSON.stringify({
                    paymentId,
                    status: newStatus,
                    paid: isPaid,
                    paymentDate: isPaid ? new Date().toISOString() : null,
                    stripePaymentIntentId: paymentIntentId,
                    stripeStatus: redirectStatus,
                    transactionDetails: {
                      completedAt: new Date().toISOString(),
                      completedBy: authenticatedUser?.email || 'cliente',
                      completionMethod: 'stripe_redirect',
                      stripeParams: Object.fromEntries(urlParams.entries()),
                      browserInfo: {
                        userAgent: navigator.userAgent,
                        language: navigator.language,
                        platform: navigator.platform
                      }
                    }
                  })
                }
              );
              
              // Debug: Logghiamo la risposta
              const responseText = await updateResponse.text();
              console.log('Risposta aggiornamento pagamento dopo redirect:', 
                updateResponse.status, 
                updateResponse.ok ? 'OK' : 'Errore',
                responseText
              );
            } else {
              console.log(`Il pagamento è già nello stato finale ${currentStatus}, nessun aggiornamento necessario`);
            }
          } else {
            console.error('Errore nel recupero dello stato del pagamento:', await checkResponse.text());
            setError('Si è verificato un errore nel recupero dello stato del pagamento');
          }
          
          setLoading(false);
          
          // Prepariamo i dati per la notifica in base allo stato del pagamento
          let notificationColor = '#4CAF50'; // Default verde
          let notificationMessage = '';
          let notificationIcon = '';
          
          // Impostiamo lo stato dell'app e prepariamo i dati della notifica
          if (success === 'true' || redirectStatus === 'succeeded') {
            setSuccess("Pagamento completato con successo!");
            notificationColor = '#4CAF50'; // Verde
            notificationMessage = 'Pagamento completato con successo!';
            notificationIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
          } else if (canceled === 'true') {
            setError("Pagamento annullato dall'utente");
            notificationColor = '#FF9800'; // Arancione
            notificationMessage = 'Pagamento annullato dall\'utente';
            notificationIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-2h-2v2zm0-4h2V7h-2v6z"/></svg>';
          } else if (error) {
            setError(`Errore durante il pagamento: ${error}`);
            notificationColor = '#F44336'; // Rosso
            notificationMessage = `Errore durante il pagamento: ${error}`;
            notificationIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>';
          } else if (redirectStatus === 'failed') {
            setError("Pagamento non riuscito");
            notificationColor = '#F44336'; // Rosso
            notificationMessage = 'Pagamento non riuscito';
            notificationIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>';
          }
          
          // Creiamo una notifica visibile
          const notification = document.createElement('div');
          notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: ${notificationColor};
            color: white;
            padding: 16px 24px;
            border-radius: 4px;
            box-shadow: 0 3px 6px rgba(0,0,0,0.16);
            z-index: 9999;
            font-family: Arial, sans-serif;
            font-size: 16px;
          `;
          notification.innerHTML = `
            <div style="display: flex; align-items: center;">
              <div style="margin-right: 12px;">
                ${notificationIcon}
              </div>
              <div>${notificationMessage}</div>
            </div>
          `;
          document.body.appendChild(notification);
          
          // Rimuoviamo la notifica dopo 5 secondi
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 5000);
          
          // Se il pagamento è completato con successo, redirigiamo alla lista dei pagamenti
          if (success === 'true' || redirectStatus === 'succeeded') {
            setTimeout(() => {
              window.location.href = '/customerPayments';
            }, 3000);
          }
        } catch (error) {
          console.error('Errore durante l\'aggiornamento dello stato dopo redirect:', error);
          setError('Si è verificato un errore nell\'aggiornamento dello stato del pagamento');
          setLoading(false);
        }
      }
    };
    
    checkForStripeRedirect();
  }, [authenticatedUser, accountId]);

  // Gestisce il successo del pagamento
  const handleSuccess = (message) => {
    setSuccess(message);
    setError(null);
    setLoading(false);
    
    // Aggiorniamo i dati di pagamento
    loadPaymentDetails();
    
    // Redirezione dopo un breve ritardo
    setTimeout(() => {
      navigate('/customer/payments');
    }, 3000);
  };
  
  // Gestisce l'errore nel pagamento
  const handleError = (message) => {
    setError(message);
    setSuccess(null);
    setLoading(false);
  };
  
  // Formatta l'importo in Euro
  const formatCurrency = (amount, isAlreadyInEuro = false) => {
    // Se l'importo è già in euro, non dividiamo per 100
    const amountInEuro = isAlreadyInEuro ? parseFloat(amount) : parseFloat(amount) / 100;
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amountInEuro);
  };
  
  // Formatta la data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const appearance = {
    theme: 'stripe',
  };
  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader title="Pagamento" />
      
      {error && (
        <Alert 
          variant="danger" 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}
      
      {success && (
        <Alert 
          variant="success" 
          message={success} 
          onClose={() => setSuccess(null)} 
        />
      )}
      
      {loading ? (
        <div className="flex justify-center my-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
          {/* Colonna sinistra - Form di pagamento */}
          <div className="w-full md:w-1/2">
            {stripePromise && clientSecret ? (
              <CheckoutProvider
                stripe={stripePromise}
                options={{
                  fetchClientSecret: () => fetchClientSecretPromise,
                  elementsOptions: { 
                    appearance: { theme: 'stripe' }
                  }
                }}
              >
                <CheckoutForm
                  onSuccess={handleSuccess}
                  onError={handleError}
                  userEmail={userEmail}
                />
              </CheckoutProvider>
            ) : (
              <div className="bg-white shadow-md rounded-lg p-6">
                <p className="font-medium mb-2">Caricamento del modulo di pagamento...</p>
                {!accountDataReady && <p className="text-sm text-amber-600 mt-2">Attesa dati account...</p>}
                {!stripePromise && <p className="text-sm text-amber-600 mt-2">Attesa inizializzazione Stripe...</p>}
                {!clientSecret && <p className="text-sm text-amber-600 mt-2">Attesa client secret dal server...</p>}
                
                <div className="mt-4 p-3 bg-gray-100 rounded text-xs">
                  <p className="font-semibold mb-1">Stato inizializzazione:</p>
                  <pre className="overflow-auto max-h-32">
                    {JSON.stringify({
                      accountDataReady: accountDataReady,
                      stripeInitialized: !!stripePromise,
                      clientSecretOttenuto: !!clientSecret,
                      idPagamento: paymentId,
                      importo: amount
                    }, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
          
          {/* Colonna destra - Dettagli pagamento */}
          <div className="w-full md:w-1/2">
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Riepilogo pagamento</h2>
              
              <div className="border-b pb-4 mb-4">
                <p className="text-lg font-semibold mb-2">
                  Importo da pagare: <span className="text-blue-600">{formatCurrency(amount, true)}</span>
                </p>
              </div>
              
              {paymentData ? (
                <div>
                  <div className="mb-4">
                    <p className="mb-2"><strong>Riferimento pagamento:</strong> {paymentData._id || paymentId}</p>
                    <p className="mb-2"><strong>Stato pagamento:</strong>
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${paymentData.paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {paymentData.paid ? "PAGATO" : "IN ATTESA"}
                      </span>
                    </p>
                    <p className="mb-2"><strong>Data creazione:</strong> {formatDate(paymentData.createdAt)}</p>
                  </div>
                  
                  {paymentData.description && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-1">Descrizione</h3>
                      <p>{paymentData.description}</p>
                    </div>
                  )}
                  
                  {paymentData.notes && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-1">Note</h3>
                      <p>{paymentData.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p>Caricamento dettagli pagamento...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}